//@flow
import type { ThunkAction } from '../../core/types'
import type { SetAction } from '../../modules/hash/index'
import type { Charity } from './types'
import {
  DOMAIN,
  RESOURCES,
  CREATE_CHARITY,
  UPDATE_CHARITY,
  FETCH_CHARITY,
  REMOVE_CHARITY
} from './constants'
import {
  isCommInProgress,
  startCommunication,
  endCommunication
} from '../../modules/communication'
import { batch } from '../../modules/lib/batchReducer'
import { services } from '../../core/feathers'
import {
  getUserId,
  saveUserCharityId,
  getUserCharityId,
  deleteUserCharityId
} from '../session'
import { normalize } from 'normalizr'
import { schemas } from '../constants'
import {
  mergeEntities,
  update,
  removeOne,
  removeMany
} from '../../modules/entities'
import { domains } from '../index'
import { hset } from '../../modules/hash/index'

function normalizeCharity(charityData: Charity) {
  return normalize(charityData, schemas.charities)
}

export function setWizardStep(step: number): SetAction {
  if (step < 1) step = 1
  if (step > 4) step = 4
  return hset(DOMAIN, 'step', step)
}

export function fetchCharity(): ThunkAction {
  return async function(dispatch, getState) {
    const resource = RESOURCES.FETCH_CHARITY
    const state = getState()
    if (isCommInProgress(resource, state)) {
      console.log('fetchCharity called but another one in progress')
      return false
    }
    const userCharityId = getUserCharityId(state)
    if (!userCharityId) {
      throw new Error('User has no registered charity')
    }
    dispatch(batch(FETCH_CHARITY.REQUEST, startCommunication(resource)))
    try {
      const charity = await services.charities.get(userCharityId, {
        query: {
          $eager: 'contacts'
        }
      })
      const { entities } = normalizeCharity(charity)
      dispatch(
        batch(
          FETCH_CHARITY.SUCCESS,
          mergeEntities(entities),
          endCommunication(resource, true)
        )
      )
    } catch (error) {
      console.warn('Error fetching user charity', error)
      dispatch(batch(FETCH_CHARITY.FAIL, endCommunication(resource, false)))
    }
  }
}

type CreateCharityData = {
  location: string,
  lat: number,
  lng: number,
  country: string,
  countryCode: string
}
export function createCharity(data: CreateCharityData): ThunkAction {
  return async function(dispatch, getState) {
    const resource = RESOURCES.CREATE_CHARITY
    const state = getState()
    if (isCommInProgress(resource, state)) {
      console.log('createCharity called but another one in progress')
      return false
    }
    const userId = getUserId(state)
    if (!userId) {
      throw new Error('User not registered')
    }

    dispatch(batch(CREATE_CHARITY.REQUEST, startCommunication(resource)))
    try {
      const charity: Charity = await services.charities.create(
        {
          userId,
          ...data
        },
        {
          query: {
            $eager: 'contacts'
          }
        }
      )
      const { entities, result } = normalizeCharity(charity)
      dispatch(
        batch(
          CREATE_CHARITY.SUCCESS,
          mergeEntities(entities),
          saveUserCharityId(result),
          endCommunication(resource, true)
        )
      )
      return charity
    } catch (feathersError) {
      console.warn('Error creating charity', feathersError)
      dispatch(batch(CREATE_CHARITY.FAIL, endCommunication(resource, false)))
      throw new Error('Create charity failed')
    }
  }
}

export function updateCharity(charityData: Object): ThunkAction {
  return async function(dispatch, getState) {
    const resource = RESOURCES.UPDATE_CHARITY
    if (isCommInProgress(resource, getState())) {
      console.log('updateCharity called but another one in progress')
      return false
    }
    const userCharityId = getUserCharityId(getState())
    if (!userCharityId) {
      throw new Error('User has no registered charity')
    }
    dispatch(batch(UPDATE_CHARITY.REQUEST, startCommunication(resource)))
    try {
      const updatedCharity = await services.charities.patch(
        userCharityId,
        charityData
      )

      dispatch(
        batch(
          UPDATE_CHARITY.SUCCESS,
          update(domains.CHARITIES, userCharityId, updatedCharity),
          endCommunication(resource, true)
        )
      )
      return updatedCharity
    } catch (feathersError) {
      console.warn('Error updating Charity', feathersError)
      dispatch(batch(UPDATE_CHARITY.FAIL, endCommunication(resource, false)))
      throw new Error('Error updating charity')
    }
  }
}

export function removeCharity(): ThunkAction {
  return async (dispatch, getState) => {
    const resource = RESOURCES.REMOVE_CHARITY
    const state = getState()
    if (isCommInProgress(resource, state)) {
      console.log('removeCharity called but another one in progress')
      return false
    }
    const userCharityId = getUserCharityId(state)
    if (!userCharityId) {
      throw new Error('User has no registered charity')
    }
    dispatch(batch(REMOVE_CHARITY.REQUEST, startCommunication(resource)))
    try {
      const removedCharity = await services.charities.remove(userCharityId)
      const removedContacts = await services.contacts.remove(null, {
        query: { charityId: userCharityId }
      })
      const removedContactIds = removedContacts.map(contact => contact.id)
      dispatch(
        batch(
          REMOVE_CHARITY.SUCCESS,
          removeOne(domains.CHARITIES, userCharityId),
          deleteUserCharityId(userCharityId),
          removeMany(domains.CONTACTS, removedContactIds),
          endCommunication(resource)
        )
      )
      return removedCharity
    } catch (feathersError) {
      console.warn('Error deleting Charity', feathersError)
      dispatch(batch(REMOVE_CHARITY.FAIL, endCommunication(resource, false)))
      throw new Error('Error deleting charity')
    }
  }
}



// WEBPACK FOOTER //
// ./src/services/register-charity/actions.js